import { Button, Card, Descriptions, Message, Space, Table, Tag } from "@arco-design/web-react";
import { Currency } from "@/components/Numeric";
import { EnumLabel } from "@/components/Enum";
import { StatusTag } from "@/components/StatusTag";
import { StatusDef, Status } from "@/enums/status";
import { TermsDef } from "@/enums/common";
import { CustomerAPI } from "@/apis/sales";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconEdit, IconCheck, IconClose, IconLeft } from "@arco-design/web-react/icon";
import Address from "@/components/Address";

export const CustomerInfo = () => {
    const params = useParams();
    const customerId = params.customerId;
    const navigate = useNavigate();
    const [customer, setCustomer] = useState({});
    const [statusLoading, setStatusLoading] = useState(false);

    const getCustomer = useCallback(async () => {
        const info = await CustomerAPI.get(customerId);
        setCustomer(info);
    }, [customerId]);

    useEffect(() => {
        getCustomer();
    }, [getCustomer]);

    const updateStatus = async (status) => {
        setStatusLoading(true);
        try {
            if (status === Status.Active) {
                await CustomerAPI.enable(customerId);
            } else {
                await CustomerAPI.disable(customerId);
            }
            Message.success("Status updated successfully");
            await getCustomer();
        } catch (error) {
            Message.error(error.message);
        } finally {
            setStatusLoading(false);
        }
    };

    return (
        <Card title={
            <Space>
                <span>{customer.customerName}</span>
                <Tag><EnumLabel enumObj={StatusDef} value={customer.status} /></Tag>
            </Space>
        } extra={
            <Space>
                {customer.status === Status.Active &&
                    <Button status="danger" icon={<IconClose />} onClick={() => updateStatus(Status.Inactive)} loading={statusLoading}>Disable</Button>
                }
                {customer.status === Status.Inactive &&
                    <Button type="text" status="success" icon={<IconCheck />} onClick={() => updateStatus(Status.Active)} loading={statusLoading}>Enable</Button>
                }
                <Button type="primary" icon={<IconEdit />} onClick={() => navigate(`/workspace/sales/customer/${customerId}/edit`)}>Edit</Button>
                <Button icon={<IconLeft />} onClick={() => navigate("/workspace/sales/customer")}>Back</Button>
            </Space>
        }>
            <Descriptions colon=":"
                column={2}
                data={[
                    { label: "Customer Name", value: customer.customerName },
                    { label: "Account Manager", value: customer.accountManagerName },
                    { label: "Term", value: <EnumLabel enumObj={TermsDef} value={customer.term} /> },
                    { label: "Credit Limit", value: <Currency value={customer.creditLimit} /> },
                ]} />
            <Descriptions column={1} layout="vertical" colon=":"
                labelStyle={{ paddingRight: 36 }}
                data={[
                    {
                        label: "Main Contact",
                        value: <Table rowKey="id" columns={[
                            { title: "First Name", dataIndex: "firstName" },
                            { title: "Last Name", dataIndex: "lastName" },
                            { title: "Tags", render: (col, record) => <StatusTag.List value={record.tags} /> },
                            { title: "Email", dataIndex: "email" },
                            { title: "Phone", dataIndex: "phone" },
                        ]} data={customer.contacts} />
                    }
                ]} />
            <Descriptions column={1} colon=":"
                labelStyle={{ paddingRight: 36 }}
                data={[
                    {
                        label: "Billing",
                        value: <Address value={{
                            address: customer.billingAddress,
                            city: customer.billingCity,
                            state: customer.billingState,
                            zip: customer.billingZip
                        }} inline />
                    }
                ]} />
            <Descriptions column={1} layout="vertical" colon=":"
                labelStyle={{ paddingRight: 36 }}
                data={[
                    {
                        label: "Shipping",
                        value: <Table rowKey="id" columns={[
                            { title: "Ship to Name", dataIndex: "contactName" },
                            { title: "Address", dataIndex: "address" },
                            { title: "City", dataIndex: "city" },
                            { title: "State", dataIndex: "state" },
                            { title: "Zip", dataIndex: "zip" },
                        ]} data={customer.shipAddresses} />
                    }
                ]} />
        </Card>
    );
};
